import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Default } from "../../components/default/Default";
import { setCollectionsList } from "../../reducer/collection/Collection.action";
import { setUserSubscription } from "../../reducer/payment/PlanPayment.action";
import PostRequest from "../../services/PostRequest";
import { numberFormatter, showToaster } from "../../common/common";
import moment from "moment";
import "./dashboard.css";

export const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const collectionList = useSelector(
    (state) => state.collectionReducer.collectionList
  );
  const _userSubscription = useSelector(
    (item) => item.planPaymentReducer.userSubscription
  );

  // Local state
  const [credit, setCredit] = useState(null);
  const [apiRequests, setApiRequests] = useState(0);
  const [apiRequestsDelta, setApiRequestsDelta] = useState(0);
  const [announcement, setAnnouncement] = useState({
    visible: true,
    title: "Officeworks availability-only fetch mode is live",
    message: "Run lighter scrapes when you only need stock status.",
  });
  const [userName, setUserName] = useState("User");
  const [loading, setLoading] = useState(true);

  // Load credits
  const loadCredits = async () => {
    try {
      const response = await PostRequest("/users/credits/");
      setCredit(response);
    } catch (error) {
      console.log("Error loading credits:", error.message);
    }
  };

  // Load API requests and usage
  const loadApiRequests = async () => {
    try {
      const currentMonth = moment().format("MM");
      const currentYear = moment().format("YYYY");
      const lastMonth = moment().subtract(1, "month");
      const lastMonthNum = lastMonth.format("MM");
      const lastMonthYear = lastMonth.format("YYYY");

      const response = await PostRequest("/users/uses/group-by-date", {
        month: currentMonth,
        year: currentYear,
      });

      const lastMonthResponse = await PostRequest("/users/uses/group-by-date", {
        month: lastMonthNum,
        year: lastMonthYear,
      });

      if (response && Array.isArray(response)) {
        const currentTotal = response.reduce((sum, item) => sum + (item.count || 0), 0);
        setApiRequests(currentTotal);

        if (lastMonthResponse && Array.isArray(lastMonthResponse)) {
          const lastTotal = lastMonthResponse.reduce(
            (sum, item) => sum + (item.count || 0),
            0
          );
          if (lastTotal > 0) {
            const delta = ((currentTotal - lastTotal) / lastTotal) * 100;
            setApiRequestsDelta(Math.round(delta * 10) / 10);
          }
        }
      }
    } catch (error) {
      console.log("Error loading API requests:", error.message);
    }
  };

  // Load user info
  const loadUserInfo = async () => {
    try {
      const auth = window.localStorage.getItem("AUTH");
      if (auth) {
        const user = JSON.parse(auth);
        setUserName(user.name || user.email || "User");
      }
    } catch (error) {
      console.log("Error loading user info:", error.message);
    }
  };

  // Initialize data
  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadUserInfo(),
          loadCredits(),
          loadApiRequests(),
          dispatch(setCollectionsList()),
          dispatch(setUserSubscription()),
        ]);
      } catch (error) {
        console.log("Error initializing dashboard:", error.message);
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, [dispatch]);

  // Calculate credit progress
  const usedCredits = credit ? Number(credit.used_credits ?? 0) : 0;
  const availableCredits = credit ? Number(credit.available_credits ?? 0) : 0;
  const remainingCredits = availableCredits - usedCredits;
  const creditProgress =
    availableCredits > 0
      ? Math.min(100, Math.round(((availableCredits - usedCredits) / availableCredits) * 100))
      : 0;
  const creditProgressUsed =
    availableCredits > 0
      ? Math.min(100, Math.round((usedCredits / availableCredits) * 100))
      : 0;

  // Get recent collections (first 4)
  const recentCollections = collectionList && collectionList.length > 0 ? collectionList.slice(0, 4) : [];

  // Format status badge
  const getStatusBadge = (status) => {
    let badgeClass = "mca-badge--soft-success";
    let label = "Completed";

    if (!status || status === "scheduled") {
      badgeClass = "mca-badge--soft-info";
      label = "Scheduled";
    } else if (status === "running" || status === "in_progress") {
      badgeClass = "mca-badge--soft-info";
      label = "Processing";
    } else if (status === "failed") {
      badgeClass = "mca-badge--soft-danger";
      label = "Failed";
    }

    return { badgeClass, label };
  };

  // Handle announcement dismiss
  const handleDismissAnnouncement = () => {
    setAnnouncement({ ...announcement, visible: false });
  };

  return (
    <Default>
      <div className="mca-content">
        {/* Welcome Header */}
        <div className="mca-page-head">
          <h1>Welcome back, {userName}</h1>
          <p>Here's what's happening across your account today.</p>
        </div>

        {/* Announcement Alert */}
        {announcement.visible && (
          <div className="mca-alert mca-alert--info" role="status" style={{ marginBottom: "24px" }}>
            <svg
              className="lead"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" />
              <path d="M12 8h.01" />
            </svg>
            <div className="mca-alert__body">
              <div className="mca-alert__title">{announcement.title}</div>
              {announcement.message} <a href="/documentation-list">Read the docs →</a>
            </div>
            <button
              className="mca-overlay-close"
              aria-label="Dismiss"
              style={{ background: "transparent" }}
              onClick={handleDismissAnnouncement}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="home-stats">
          {/* API Requests Card */}
          <div className="mca-card stat">
            <div className="stat__top">
              <div
                className="stat__icon"
                style={{
                  background: "var(--mca-primary-subtle-bg)",
                  color: "var(--mca-link)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
            </div>
            <div className="stat__label">API requests this month</div>
            <div className="stat__value">{numberFormatter.format(apiRequests)}</div>
            {apiRequestsDelta !== 0 && (
              <div
                className="stat__delta"
                style={{
                  color: apiRequestsDelta > 0 ? "var(--mca-success)" : "var(--mca-danger)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {apiRequestsDelta > 0 ? (
                    <>
                      <path d="M7 17 17 7" />
                      <path d="M9 7h8v8" />
                    </>
                  ) : (
                    <>
                      <path d="M17 7 7 17" />
                      <path d="M15 17H7v-8" />
                    </>
                  )}
                </svg>
                {Math.abs(apiRequestsDelta).toFixed(1)}% vs last month
              </div>
            )}
          </div>

          {/* Active Collections Card */}
          <div className="mca-card stat">
            <div className="stat__top">
              <div
                className="stat__icon"
                style={{
                  background: "var(--mca-success-subtle)",
                  color: "var(--mca-success)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M8 5h13M8 12h13M8 19h13M3.5 5h.01M3.5 12h.01M3.5 19h.01" />
                </svg>
              </div>
            </div>
            <div className="stat__label">Active collections</div>
            <div className="stat__value">{collectionList ? collectionList.length : 0}</div>
            {collectionList && collectionList.length > 0 && (
              <div className="stat__delta" style={{ color: "var(--mca-muted)" }}>
                {collectionList.length > 3 ? "3" : collectionList.length} active in your account
              </div>
            )}
          </div>

          {/* Credits Remaining Card */}
          <div className="mca-card stat">
            <div className="stat__top">
              <div
                className="stat__icon"
                style={{
                  background: "var(--mca-warning-subtle)",
                  color: "#b8770a",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </div>
            </div>
            <div className="stat__label">Credits remaining</div>
            <div className="stat__value">
              {credit ? numberFormatter.format(remainingCredits) : "—"}
            </div>
            {credit && (
              <>
                <div className="mca-progress" style={{ marginTop: "12px" }}>
                  <div className="mca-progress__bar" style={{ width: `${creditProgress}%` }}></div>
                </div>
                <div className="stat__delta" style={{ color: "var(--mca-muted)" }}>
                  {numberFormatter.format(usedCredits)} of {numberFormatter.format(availableCredits)} used
                  {credit.period_end ? ` · resets ${moment(credit.period_end).format("MMM D")}` : ""}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recent Runs & Quick Actions */}
        <div className="home-grid">
          {/* Recent Runs Card */}
          <div className="mca-card">
            <div className="mca-card__head">
              <div className="mca-card__title">Recent runs</div>
              <Link className="mca-btn mca-btn--link mca-btn--sm" to="/collections">
                View all
              </Link>
            </div>
            <div className="mca-card__body" style={{ paddingTop: "6px", paddingBottom: "6px" }}>
              {recentCollections.length > 0 ? (
                recentCollections.map((collection, idx) => {
                  const status = getStatusBadge(collection.status);
                  const lastRun = collection.last_run ? moment(collection.last_run) : null;
                  const timeAgo = lastRun ? lastRun.fromNow() : "never";

                  return (
                    <div className="activity-row" key={idx}>
                      <span className={`mca-badge ${status.badgeClass}`}>
                        <span className="dot"></span>
                        {status.label}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div className="name">{collection.name}</div>
                        <div className="meta">
                          {collection.request_type || "Custom"} · {collection.request_count || 0} records · {timeAgo}
                        </div>
                      </div>
                      <span className="mca-cell-mono cell-muted">
                        {collection.cost_per_run ? `${collection.cost_per_run} cr` : "—"}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: "20px", textAlign: "center", color: "var(--mca-muted)" }}>
                  <p>No collections yet. Create your first collection to get started.</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="mca-card mca-card--pad quick">
            <div className="mca-card__title" style={{ marginBottom: "8px" }}
            >
              Quick actions
            </div>
            <Link to="/playground">
              <svg
                className="lead"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2.5" y="4" width="19" height="14" rx="2" />
                <path d="m7 9 2.5 2.2L7 13.4" />
                <path d="M12.5 13.4h4" />
              </svg>
              <span>
                <span className="t">Test an API call</span>
                <br />
                <span className="d">Try Amazon or Officeworks live</span>
              </span>
              <svg
                className="arr"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 6 6 6-6 6" />
              </svg>
            </Link>
            <Link to="/collections">
              <svg
                className="lead"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span>
                <span className="t">New collection</span>
                <br />
                <span className="d">Schedule a recurring batch</span>
              </span>
              <svg
                className="arr"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 6 6 6-6 6" />
              </svg>
            </Link>
            <Link to="/account">
              <svg
                className="lead"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
                <path d="M2.5 9.5h19" />
              </svg>
              <span>
                <span className="t">Top up credits</span>
                <br />
                <span className="d">Manage plan &amp; billing</span>
              </span>
              <svg
                className="arr"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 6 6 6-6 6" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </Default>
  );
};
